import * as React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';

import Loader from '../../shared/components/Loader';
import t from '../../shared/translations';
import Error from '../components/Error';
import CopiedText from './CopiedText';
import QrButton from './QrButton';
import { IPulseArgs, IPulseData, pulseQuery } from './queries';
import { AnswerBox, AnswerGrid, CopyBox, CopyRow, Maw } from './stylings';

interface IProps {
    refetch?: VoidFunction;
}

type IPulse = IPulseData['pulse'];

function assembleAnswers(pulse: IPulse): Map<string, string[]> {
    const result: Map<string, string[]> = new Map();
    result.set(pulse.question1, []);
    if (pulse.question2) {
        result.set(pulse.question2, []);
    }
    for (const ans of pulse.pulseAnswers) {
        result.get(pulse.question1).push(ans.answer1);
        if (pulse.question2) {
            result.get(pulse.question2).push(ans.answer2);
        }
    }
    return result;
}

function PulseResponse({ refetch }: IProps): JSX.Element {
    const { id } = useParams();
    const [result, queryExecute] = useQuery<IPulseData, IPulseArgs>({
        query: pulseQuery,
        requestPolicy: 'cache-and-network',
        variables: { id },
    });
    const [copied, setCopied] = React.useState(false);

    const showCopied = () => {
        setCopied(true);
    };

    React.useEffect(() => {
        const timer = setInterval(() => queryExecute(), 20000);
        return () => {
            clearInterval(timer);
        };
    }, []);

    React.useEffect(() => {
        if (result.data?.pulse?.pulseAnswers?.length > 0 && refetch) {
            refetch();
        }
    }, [result]);

    let main = null;
    if (result.fetching) {
        main = <Loader />;
    } else if (result.error) {
        main = <Error />;
    } else {
        const pulse = result.data.pulse;
        const answersMap = assembleAnswers(pulse);
        main = <>
            <CopyBox>
                <p>{t('player.pulse-results.info-copy')}</p>
                <CopyRow>
                    <input value={pulse.link} readOnly />
                    <CopyToClipboard text={pulse.link} onCopy={showCopied}>
                        <button>{t('player.pulse-results.button-copy-link')}</button>
                    </CopyToClipboard>
                    {copied && <CopiedText setCopied={setCopied} />}
                </CopyRow>
                <QrButton pulse={pulse} />
            </CopyBox>
            <p>{t('player.pulse-results.info-results')}</p>
            {pulse.pulseAnswers.length === 0 && <div>
                {t('player.pulse-results.info-no-answers')}
            </div>}
            {pulse.pulseAnswers.length > 0 && <AnswerGrid>
                {Array.from(answersMap.keys()).map(question => <AnswerBox key={question}>
                    <h3>{question}</h3>
                    {answersMap.get(question).map((ans, idx) => <p key={idx}>{ans}</p>)}
                </AnswerBox>)}
            </AnswerGrid>}
        </>;
    }
    return <Maw>
        {main}
    </Maw>;
}

export default PulseResponse;
